import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Alert,
    Input,
    Label,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { resolvePlanPricing } from "../../../utils/planPricing";

/**
 * DowngradeInfoModal - Plan düşürme bilgilendirme ve onay modali
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen              - Modal açık mı?
 * @param {Function} toggle              - Modal'ı kapat/aç
 * @param {Object}   currentPlan         - Mevcut plan (name, maxAccounts, maxUsers, fiyat alanları)
 * @param {Object}   targetPlan          - Hedef plan (id, name, maxAccounts, maxUsers, fiyat alanları)
 * @param {Object}   currentSubscription - Mevcut abonelik (endDate, currentAccounts, currentUsers)
 * @param {Function} onConfirm           - Onay callback (targetPlanId, reason)
 * @param {boolean}  loading             - Yükleniyor durumu
 */
const DowngradeInfoModal = ({
    isOpen,
    toggle,
    currentPlan,
    targetPlan,
    currentSubscription,
    onConfirm,
    loading,
}) => {
    const [reason, setReason] = useState("");

    if (!currentPlan || !targetPlan) return null;

    const formatPrice = (price) =>
        new Intl.NumberFormat("tr-TR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price) + " ₺";

    const formatDate = (d) =>
        d ? new Date(d).toLocaleDateString("tr-TR") : "-";

    const formatLimit = (val) => (val ? val.toLocaleString("tr-TR") : "Sınırsız");

    // Limit aşımı kontrolü
    const currentAccounts = currentSubscription?.currentAccounts || 0;
    const currentUsers = currentSubscription?.currentUsers || 0;
    const targetMaxAccounts = targetPlan.maxAccounts;
    const targetMaxUsers = targetPlan.maxUsers;

    const accountsExceeded = targetMaxAccounts != null && currentAccounts > targetMaxAccounts;
    const usersExceeded = targetMaxUsers != null && currentUsers > targetMaxUsers;
    const limitsExceeded = accountsExceeded || usersExceeded;

    const endDate = currentSubscription?.endDate || currentPlan?.currentPlanEndDate;
    // Ücretli pakete düşürme ödeme adımından geçer; ödemesiz planlama yalnız Ücretsiz pakete.
    const requiresPayment = (targetPlan.totalPrice || 0) > 0;

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm(targetPlan.id, reason || null);
        }
        setReason("");
    };

    const handleClose = () => {
        setReason("");
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
            <ModalHeader toggle={handleClose} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-warning">
                    <i className="bx bx-down-arrow-circle fs-4"></i>
                    Plan Düşürme
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                {/* Uyarı */}
                <Alert color="warning" className="d-flex align-items-center gap-2 mb-4">
                    <i className="bx bx-info-circle fs-5"></i>
                    <span>Bu plan mevcut planınızdan düşüktür</span>
                </Alert>

                {/* Karşılaştırma tablosu */}
                <div className="border rounded mb-3" style={{ overflow: "hidden" }}>
                    <Table className="table table-bordered mb-0">
                        <Thead>
                            <Tr style={{ backgroundColor: "#f9f9f9" }}>
                                <Th style={{ border: 0, color: "#6c757d", fontWeight: 600, width: "34%" }}></Th>
                                <Th style={{ border: 0, color: "#6c757d", fontWeight: 600, width: "33%", textAlign: "center" }}>
                                    Mevcut Plan
                                </Th>
                                <Th style={{ border: 0, color: "#6c757d", fontWeight: 600, width: "33%", textAlign: "center" }}>
                                    Yeni Plan
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td className="fw-medium text-muted">Plan Adı</Td>
                                <Td className="text-center fw-semibold">{currentPlan.name}</Td>
                                <Td className="text-center fw-semibold">{targetPlan.name}</Td>
                            </Tr>
                            <Tr>
                                <Td className="fw-medium text-muted">Maks. Cari</Td>
                                <Td className="text-center">{formatLimit(currentPlan.maxAccounts)}</Td>
                                <Td className="text-center" style={{
                                    color: accountsExceeded ? "#dc3545" : undefined,
                                    fontWeight: accountsExceeded ? 600 : undefined,
                                }}>
                                    {formatLimit(targetPlan.maxAccounts)}
                                    {accountsExceeded && <i className="bx bx-error-circle ms-1"></i>}
                                </Td>
                            </Tr>
                            <Tr>
                                <Td className="fw-medium text-muted">Maks. Kullanıcı</Td>
                                <Td className="text-center">{formatLimit(currentPlan.maxUsers)}</Td>
                                <Td className="text-center" style={{
                                    color: usersExceeded ? "#dc3545" : undefined,
                                    fontWeight: usersExceeded ? 600 : undefined,
                                }}>
                                    {formatLimit(targetPlan.maxUsers)}
                                    {usersExceeded && <i className="bx bx-error-circle ms-1"></i>}
                                </Td>
                            </Tr>
                            <Tr>
                                <Td className="fw-medium text-muted">Fiyat</Td>
                                {[currentPlan, targetPlan].map((plan) => {
                                    const { payable, struck } = resolvePlanPricing(plan);
                                    return (
                                        <Td key={plan.id || plan.name} className="text-center">
                                            {struck != null && (
                                                <div className="text-muted small" style={{ textDecoration: "line-through" }}>
                                                    {formatPrice(struck)}
                                                </div>
                                            )}
                                            {formatPrice(payable)}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        </Tbody>
                    </Table>
                </div>

                {/* Limit aşımı uyarısı */}
                {limitsExceeded && (
                    <Alert color="danger" className="d-flex align-items-start gap-2 mb-3">
                        <i className="bx bx-error fs-5 mt-1"></i>
                        <span>
                            <strong>Dikkat:</strong> Mevcut cari/kullanıcı sayınız yeni planın limitlerini aşıyor.
                            Plan değişikliğinden sonra yeni kayıt ekleyemezsiniz.
                        </span>
                    </Alert>
                )}

                {/* Bilgilendirme */}
                <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex align-items-start gap-2">
                        <i className="bx bx-calendar-check text-primary fs-5 mt-1"></i>
                        {requiresPayment ? (
                            <p className="mb-0" style={{ fontSize: ".9rem" }}>
                                <strong>{targetPlan.name}</strong> paketinin bedeli (<strong>{formatPrice(targetPlan.totalPrice)}</strong>, KDV dahil)
                                şimdi ödenir. Mevcut planınız <strong>{formatDate(endDate)}</strong> tarihine kadar sürer,
                                yeni paketiniz o tarihten itibaren başlar.
                            </p>
                        ) : (
                            <p className="mb-0" style={{ fontSize: ".9rem" }}>
                                Mevcut planınız <strong>{formatDate(endDate)}</strong> tarihinde sona erdiğinde
                                otomatik olarak <strong>{targetPlan.name}</strong> planına geçilecektir.
                            </p>
                        )}
                    </div>
                </div>

                {/* Sebep */}
                {!requiresPayment && (
                <div>
                    <Label className="text-muted mb-1" style={{ fontSize: ".9rem" }}>
                        Düşürme Sebebi <span className="text-muted">(İsteğe bağlı)</span>
                    </Label>
                    <Input
                        type="textarea"
                        rows={3}
                        placeholder="Düşürme sebebinizi belirtebilirsiniz..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={500}
                    />
                </div>
                )}
            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        color="secondary"
                        onClick={handleClose}
                        disabled={loading}
                        className="px-4"
                    >
                        Vazgeç
                    </Button>
                    <Button
                        color="warning"
                        onClick={handleConfirm}
                        disabled={loading}
                        className="px-4 d-flex align-items-center gap-2 text-white"
                        style={{ minWidth: "140px" }}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" />
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <i className={`bx ${requiresPayment ? "bx-credit-card" : "bx-check-circle"}`}></i>
                                {requiresPayment ? "Ödemeye Geç" : "Onaylıyorum"}
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default DowngradeInfoModal;
